import React, { useState, useEffect } from "react";
import {Popover, Button, Spin, Select, Row, Space, Badge, Typography, Layout} from "antd";

import dayjs from "dayjs";
import { fetchWrapper } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../../context/organizationContext";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {ImeanHeader} from "../../components/Header/Header";
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const SOCIAL_ICON_LINKS = {
    linkedin: "https://cdn.builder.io/api/v1/image/assets/TEMP/d25408c5dff59b86cb5e75674b2a798a87cd455bde0adf0ceb8c9a670d1405e8?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
    facebook: "https://cdn.builder.io/api/v1/image/assets/TEMP/f8432315b7e3752ab28ffd9571f771342eef6b676c1f20e4b8a5e6ee48c70349?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
    instagram: "https://cdn.builder.io/api/v1/image/assets/TEMP/497038a4df4615b7ef35db19c49364c0fdcd7e3749f9681fe807b55de2426e88?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
};

const CalendarPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const org = useOrganization();
    const user = useSelector((obj) => obj.auth.user);

    const getInitialDateRange = () => {
        try {
            const savedRange = localStorage.getItem("calendarDatesRange");
            if (savedRange) {
                const parsedRange = JSON.parse(savedRange);
                return {
                    pastDays: parsedRange.pastDays || 1,
                    futureDays: parsedRange.futureDays || 1,
                };
            }
        } catch (error) {
            console.error("Error loading date range from localStorage:", error);
        }
        return { pastDays: 1, futureDays: 1 };
    };

    const initialRange = getInitialDateRange();
    const [pastDays, setPastDays] = useState(initialRange.pastDays);
    const [futureDays, setFutureDays] = useState(initialRange.futureDays);
    const [calendarDays, setCalendarDays] = useState([]);
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [orgUsers, setOrgUsers] = useState([]);

    // Fetch organization users
    useEffect(() => {
        if (org?.organization?.employees && org?.organization?.moderatorExist && user?.moderatorOrg) {
            setOrgUsers(org.organization.employees);
        }
    }, [org, user]);

    useEffect(() => {
        try {
            localStorage.setItem("calendarDatesRange", JSON.stringify({ pastDays, futureDays }));
        } catch (error) {
            console.error("Error saving date range to localStorage:", error);
        }
    }, [pastDays, futureDays]);

    useEffect(() => {
        const today = dayjs();
        const startDate = today.subtract(pastDays, "day").startOf("day");
        const endDate = today.add(futureDays, "day").endOf("day");
        setIsLoading(true);
        fetchWrapper
            .get(`${baseUrl}/post/calendar?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`)
            .then((data) => {
                setPosts(data || []);
            })
            .catch((error) => {
                console.error("Error fetching posts:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [pastDays, futureDays]);

    useEffect(() => {
        const today = dayjs();
        const startDate = today.subtract(pastDays, "day");
        const endDate = today.add(futureDays, "day");
        const days = [];
        let currentDate = startDate;

        const latestPostsByContentPlanId = {};

        posts.forEach(post => {
            const dateKey = dayjs(post.date).format("YYYY-MM-DD");
            const key = `${post.contentPlanId}_${dateKey}`;

            if (!latestPostsByContentPlanId[key] || post.id > latestPostsByContentPlanId[key].id) {
                latestPostsByContentPlanId[key] = post;
            }
        });

        while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, "day")) {
            const currentDateFormatted = currentDate.format("YYYY-MM-DD");

            let postsForDay = Object.values(latestPostsByContentPlanId).filter(post => {
                const postDate = dayjs(post.date);
                return postDate.format("YYYY-MM-DD") === currentDateFormatted;
            });

            // Apply user filter only to organization posts
            if (selectedUserId && selectedUserId !== '') {
                postsForDay = postsForDay.filter(post => {
                    // Keep personal posts (not organization posts)
                    if (!post.isOrg) return true;
                    // Filter organization posts by selected user
                    // Dangerous - can cause collisions if users have same names. Fix requried. Tight deadline.
                    return post.username === orgUsers.find((element)=> element.id===selectedUserId).fullName || post.UserId === selectedUserId;
                });
            }

            days.push({
                date: currentDate,
                posts: postsForDay,
            });
            currentDate = currentDate.add(1, "day");
        }
        setCalendarDays(days);
    }, [pastDays, futureDays, posts, selectedUserId]);

    const handleSetPastDays = (days) => {
        setPastDays(days);
    };

    const handleSetFutureDays = (days) => {
        setFutureDays(days);
    };

    const getPostBgColor = (contentType, isOrg) => {
        if (contentType === "Post" && !isOrg) {
            return "bg-purple-100";
        } else if (contentType === "Article" && !isOrg) {
            return "bg-blue-100";
        } else if (contentType === "Post" && isOrg) {
            return "bg-orange-100";
        } else if (contentType === "Article" && isOrg) {
            return "bg-yellow-100";
        } else {
            return "bg-gray-100";
        }
    };

    const getTruePostStatus = (status, error) => {
        const errMap = Object.keys(error);
        if (status === "error" || errMap.length) {
            return "error";
        } else {
            return status;
        }
    };

    const getBorderColor = (contentType, isOrg, status) => {
        if (status === "error") {
            return "border-red-500";
        }
        if (status === "published") {
            return "border-green-500";
        }

        if (contentType === "Post" && !isOrg) {
            return "border-purple-500";
        } else if (contentType === "Article" && !isOrg) {
            return "border-blue-500";
        } else if (contentType === "Post" && isOrg) {
            return "border-orange-500";
        } else if (contentType === "Article" && isOrg) {
            return "border-yellow-500";
        } else {
            return "border-gray-500";
        }
    };

    const getPostTextColor = (contentType, isOrg) => {
        if (contentType === "Post" && !isOrg) {
            return "text-purple-700";
        } else if (contentType === "Article" && !isOrg) {
            return "text-blue-700";
        } else if (contentType === "Post" && isOrg) {
            return "text-orange-700";
        } else if (contentType === "Article" && isOrg) {
            return "text-yellow-700";
        } else {
            return "text-gray-700";
        }
    };

    const isNavButtonActive = (days, direction) => {
        return (direction === "past" && days === pastDays) || (direction === "future" && days === futureDays);
    };

    const getNavButtonClass = (days, direction) => {
        const baseClass = "px-2 py-1 text-sm font-medium rounded mx-1 transition-colors duration-200";
        return isNavButtonActive(days, direction) ? `${baseClass} bg-violet-600 text-white` : `${baseClass} bg-gray-200 hover:bg-gray-300 text-gray-700`;
    };

    const getPostContent = (post) => {
        return (
            <div className="max-w-[300px] sm:max-w-[400px]">
                <h3 className="max-w-sm text-violet-800 text-sm md:text-md font-semibold">
                    <strong>{post.title}</strong>
                </h3>

                {/*{getTruePostStatus(post.status, post.error)  === 'error' && post.error && (*/}

                <div className="mt-1 flex flex-col gap-1">
                    {post?.sn?.map((sn) => (
                        <div key={sn} className={`${post.error[sn.toLowerCase()] ? "bg-red-50 border-red-100" : post.status === "pending" ? "bg-gray-50 border-gray-100" : "bg-green-50 border-green-100"} p-1 border flex gap-1.5 rounded text-xs overflow-hidden`}>
                            <img className="w-4 h-4" key={sn} src={SOCIAL_ICON_LINKS[sn.toLowerCase()]} alt={sn} />
                            {post.error[sn.toLowerCase()] ? (
                                <div>
                                    <p className="font-semibold text-xs text-red-800">{t("publicationCalendar.postStatuses.error")}</p>
                                    <p className="text-xs text-red-800 max-w-[300px] text-wrap text-pretty">{post.error[sn.toLowerCase()]}</p>
                                </div>
                            ) : post.status === "pending" ? (
                                <p>{t("publicationCalendar.postStatuses.pending")}</p>
                            ) : (
                                <p className="font-medium text-xs text-green-800">{t("publicationCalendar.postStatuses.published")}</p>
                            )}
                        </div>
                    ))}
                </div>

                <div className="my-4 flex justify-between gap-2 items-center">
                    <p className="text-sm font-medium text-black/50">{post.username}</p>
                    <p className="text-xs text-right text-black/25">{dayjs(post.date).format("YYYY-MM-DD HH:mm")}</p>
                </div>

                <div className="mt-4">
                    <Button type="primary" className="w-full bg-violet-600 hover:!bg-violet-800 " onClick={() => navigate(`/create-post/${post.contentPlanId}`)}>
                        {t("publicationCalendar.goToPost")}
                    </Button>
                </div>
            </div>
        );
    };

    const getPostTypeLabel = (contentType, isOrg) => {
        if (contentType === "Post" && !isOrg) {
            return `${t("publicationCalendar.postTypes.quickPersonal")} ${!!(org?.organization?.moderatorExist && user?.moderatorOrg) ? t("publicationCalendar.postTypes.personal") : ""}`;
        } else if (contentType === "Article" && !isOrg) {
            return `${t("publicationCalendar.postTypes.planPersonal")} ${!!(org?.organization?.moderatorExist && user?.moderatorOrg) ? t("publicationCalendar.postTypes.personal") : ""}`;
        } else if (contentType === "Post" && isOrg) {
            return t("publicationCalendar.postTypes.quickEmployee");
        } else if (contentType === "Article" && isOrg) {
            return t("publicationCalendar.postTypes.planEmployee");
        } else {
            return t("publicationCalendar.postTypes.other");
        }
    };

    const today = dayjs();

    return (
        <>
            {/* Header */}
            <div className="w-full">
                <ImeanHeader initialDark={true} />
                <StickyNavigationHeader headerTitle={t('home.postsCalendar')} onBackClick={() => navigate('/home')}></StickyNavigationHeader>
            </div>

            {/* Moderation user filter (kept functional, restyled) */}
            <div className="px-4 sm:px-6 md:px-8">
                {org?.organization?.moderatorExist && user?.moderatorOrg && (
                    <div className="flex justify-center items-center mb-4">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{t("publicationCalendar.filters.byUser")}</span>
                            <Select
                                placeholder={t("publicationCalendar.filters.allUsers")}
                                value={selectedUserId}
                                onChange={(value) => setSelectedUserId(value)}
                                style={{ minWidth: 220 }}
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    { value: "", label: t("publicationCalendar.filters.allUsers") },
                                    ...orgUsers.map((u) => ({
                                        value: u.id,
                                        label: u.fullName || u.email,
                                    })),
                                ]}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Toolbar — Google Calendar vibe */}
            <div className="px-4 sm:px-6 md:px-8">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between bg-white/80 backdrop-blur-md border border-gray-200 rounded-xl p-3">
                    {/* Left controls */}
                    <div className="flex items-center gap-2">
                        <Button
                            className="!rounded-lg"
                            onClick={() => {
                                handleSetPastDays(0);
                                handleSetFutureDays(0);
                            }}
                        >
                            {t("publicationCalendar.toolbar.today")}
                        </Button>

                        {/* Quick range chips */}
                        <div className="hidden sm:flex items-center gap-1">
                            <span className="text-xs text-gray-500 mr-1">{t("publicationCalendar.toolbar.past")}</span>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(3, "past") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetPastDays(3)}
                            >
                                3
                            </Button>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(7, "past") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetPastDays(7)}
                            >
                                7
                            </Button>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(30, "past") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetPastDays(30)}
                            >
                                30
                            </Button>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(60, "past") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetPastDays(60)}
                            >
                                60
                            </Button>

                            <span className="text-xs text-gray-500 mx-2">•</span>

                            <span className="text-xs text-gray-500 mr-1">{t("publicationCalendar.toolbar.future")}</span>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(3, "future") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetFutureDays(3)}
                            >
                                3
                            </Button>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(7, "future") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetFutureDays(7)}
                            >
                                7
                            </Button>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(30, "future") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetFutureDays(30)}
                            >
                                30
                            </Button>
                            <Button
                                className={`!px-2 !py-1 !text-xs !rounded-md ${isNavButtonActive(60, "future") ? "!bg-violet-600 !text-white" : ""}`}
                                onClick={() => handleSetFutureDays(60)}
                            >
                                60
                            </Button>
                        </div>
                    </div>

                    {/* Center range label */}
                    <div className="text-center">
                        <div className="text-sm md:text-base font-medium text-gray-700">
                            {dayjs().subtract(pastDays, "day").format("MMM D")} –{" "}
                            {dayjs().add(futureDays, "day").format("MMM D, YYYY")}
                        </div>
                    </div>

                    {/* Right: View select (Day/Week/Month/Year) */}
                    <div className="flex items-center gap-2 justify-end">
                        <span className="hidden sm:inline text-sm text-gray-500">{t("publicationCalendar.toolbar.view")}</span>
                        <Select
                            style={{ minWidth: 160 }}
                            value={
                                pastDays === 0 && futureDays === 0
                                    ? t("publicationCalendar.toolbar.viewOptions.day")
                                    : pastDays === 3 && futureDays === 3
                                        ? t("publicationCalendar.toolbar.viewOptions.week")
                                        : pastDays === 30 && futureDays === 30
                                            ? t("publicationCalendar.toolbar.viewOptions.month")
                                            : pastDays >= 180 && futureDays >= 180
                                                ? t("publicationCalendar.toolbar.viewOptions.year")
                                                : t("publicationCalendar.toolbar.viewOptions.custom")
                            }
                            onChange={(v) => {
                                if (v === "day") {
                                    handleSetPastDays(0);
                                    handleSetFutureDays(0);
                                } else if (v === "week") {
                                    handleSetPastDays(3);
                                    handleSetFutureDays(3);
                                } else if (v === "month") {
                                    handleSetPastDays(30);
                                    handleSetFutureDays(30);
                                } else if (v === "year") {
                                    handleSetPastDays(180);
                                    handleSetFutureDays(180);
                                }
                            }}
                            options={[
                                { value: "day", label: t("publicationCalendar.toolbar.viewOptions.day") },
                                { value: "week", label: t("publicationCalendar.toolbar.viewOptions.week") },
                                { value: "month", label: t("publicationCalendar.toolbar.viewOptions.month") },
                                { value: "year", label: t("publicationCalendar.toolbar.viewOptions.year") },
                                { value: "custom", label: t("publicationCalendar.toolbar.viewOptions.custom") },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Calendar body */}
            <div className="calendar-page sm:p-2 md:p-4 overflow-auto">
                {/* Loading */}
                <div className="max-h-[calc(100vh-270px)] calendar-grid-container overflow-auto border border-gray-200 rounded-xl">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center gap-5 h-40">
                            <Spin size="large" />
                            <p className="text-gray-500">Loading posts…</p>
                        </div>
                    ) : (
                        <div className="calendar-grid grid grid-flow-col auto-cols-[minmax(220px,1fr)]">
                            {calendarDays.map((day, index) => (
                                <div
                                    key={index}
                                    className={`calendar-column border-r border-gray-200 min-w-[220px] ${
                                        day.date.isSame(today, "day") ? "bg-blue-50/40" : "bg-white"
                                    }`}
                                >
                                    {/* Column header like Google Calendar */}
                                    <div
                                        className={`sticky top-0 z-10 flex flex-col items-center justify-center gap-0.5 p-3 border-b border-gray-200 ${
                                            day.date.isSame(today, "day") ? "bg-blue-50/60" : "bg-white/80"
                                        } backdrop-blur`}
                                    >
                                        <div className="text-xs uppercase tracking-wide text-gray-500">
                                            {day.date.format("ddd")}
                                        </div>
                                        <div
                                            className={`text-2xl font-semibold ${
                                                day.date.isSame(today, "day") ? "text-blue-700" : "text-gray-800"
                                            }`}
                                        >
                                            {day.date.format("D")}
                                        </div>
                                        <div className="text-[11px] text-gray-400">{day.date.format("MMM YYYY")}</div>
                                    </div>

                                    {/* Events */}
                                    <div className="day-posts p-3 space-y-3 min-h-[560px]">
                                        {day.posts.length === 0 ? (
                                            <div className="text-center text-gray-300 text-sm pt-6">
                                                {t("publicationCalendar.noPosts")}
                                            </div>
                                        ) : (
                                            day.posts.map((post) => (
                                                <Popover
                                                    key={post.id}
                                                    content={getPostContent(post)}
                                                    title={getPostTypeLabel(post.contentType, post.isOrg)}
                                                    trigger="hover"
                                                    placement="bottom"
                                                >
                                                    <div
                                                        className={`cursor-pointer post-item p-2 rounded-lg border-l-4 shadow-sm transition-transform hover:scale-[1.01] ${getPostBgColor(
                                                            post.contentType,
                                                            post.isOrg
                                                        )} ${getBorderColor(
                                                            post.contentType,
                                                            post.isOrg,
                                                            getTruePostStatus(post.status, post.error)
                                                        )}`}
                                                    >
                                                        <div className="flex justify-between gap-2">
                                                            <div className="post-time text-xs text-gray-500">
                                                                {dayjs(post.date).format("HH:mm")}
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                {post?.sn?.map((sn) => (
                                                                    <span
                                                                        key={sn}
                                                                        className={`${
                                                                            post.error[sn.toLowerCase()]
                                                                                ? "bg-red-500 shadow-red-500"
                                                                                : post.status === "pending"
                                                                                    ? "bg-gray-300 shadow-gray-300"
                                                                                    : "bg-green-500 shadow-green-500"
                                                                        } w-5 h-5 aspect-square shadow flex items-center justify-center rounded-full`}
                                                                    >
                                  <img
                                      src={SOCIAL_ICON_LINKS[sn.toLowerCase()]}
                                      alt={sn}
                                      className="w-4 h-4 aspect-square"
                                  />
                                </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`mt-1 post-title text-sm font-medium ${getPostTextColor(
                                                                post.contentType,
                                                                post.isOrg
                                                            )}`}
                                                        >
                                                            {post.title}
                                                        </div>
                                                    </div>
                                                </Popover>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="calendar-legend mt-4 flex flex-wrap gap-3 justify-center text-xs">
                    <div className="flex items-center">
                        <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1" />
                        <span>{t("publicationCalendar.legendLabels.quickPersonal")}</span>
                    </div>
                    <div className="flex items-center">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1" />
                        <span>{t("publicationCalendar.legendLabels.plannedPersonal")}</span>
                    </div>
                    {org?.organization?.moderatorExist && user?.moderatorOrg && (
                        <>
                            <div className="flex items-center">
                                <span className="inline-block w-3 h-3 rounded-full bg-orange-500 mr-1" />
                                <span>{t("publicationCalendar.legendLabels.quickEmployee")}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-1" />
                                <span>{t("publicationCalendar.legendLabels.plannedEmployee")}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );

};

export default CalendarPage;
